import React from 'react'
import styled from '@emotion/styled'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { Button } from '@mobi/component-library/Base/Buttons'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { hexColors } from '@mobi/settings'
import type { SearchModeType } from '@core/Areas/Search/types'

export const SearchField: React.FC<{
  value: string
  searchMode: SearchModeType
  onValueChange: (textVal: string) => void
  onClearResults: () => void
}> = ({ value, searchMode, onValueChange, onClearResults }) => {
  const inputFieldRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    inputFieldRef.current?.focus()
  }, [])

  const onKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    event.key === 'Enter' && inputFieldRef.current?.blur()
  }

  const onSearchValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onValueChange(event.target.value)
  }

  const onClearText = () => {
    onClearResults()
    inputFieldRef.current?.focus()
  }

  return (
    <WrapperStyled data-testid='SearchField'>
      <Icon name='LineSearchSm' color={hexColors.white} size='2rem' />

      <form onSubmit={e => e.preventDefault()} action='.'>
        <input
          autoFocus
          value={value}
          onKeyUp={onKeyUp}
          ref={inputFieldRef}
          data-testid='SearchField.Input'
          onChange={onSearchValueChange}
          placeholder={`Search for anything ${searchMode}`}
        />
      </form>

      {value && (
        <Button
          size='sm'
          isIconOnlyButton
          color='tertiary_dark'
          onClick={onClearText}
          data-testid='SearchField.Clear'
        >
          <Icon name='SolidXClose' color={colors.white} size='2rem' />
        </Button>
      )}
    </WrapperStyled>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  flex: '1',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: spacing.smx1,
  height: '4rem',
  minHeight: '4rem',
  paddingInline: spacing.sm,
  borderRadius: radius.lg,
  fontFamily: font.family.primary,
  backgroundColor: colors.studio[600],

  '> form': {
    flex: 1,
    display: 'flex',
    alignSelf: 'stretch',
    paddingBlock: spacing.smx2,

    '> input': {
      border: 'none',
      padding: 0,
      width: '100%',
      marginRight: `calc(3.6rem - ${spacing.smx2})`,
      fontSize: font.size.lg.fontSize,
      letterSpacing: font.size.lg.letterSpacing,
      lineHeight: font.size.lg.lineHeight,
      fontWeight: font.weight.regular,
      fontFamily: font.family.primary,
      color: colors.white,
      background: 0,

      ':focus': { outline: 'none' },
      '::placeholder': { color: colors.lavender[200] },
    },
  },
})
